import { lazy, Suspense, type ComponentType } from "react";
import { PageSkeleton } from "../components/Skeleton";

// Route-level code splitting (release 1.5.0, ported from Summit Engineering's page() helper in App.tsx, D-0189). Each
// screen is its own chunk, downloaded on first visit (or earlier, when its link is hovered — lib\preload.ts). While the
// chunk arrives the page shows the same skeleton it shows while its data arrives.

const RELOAD_KEY = "summitops.chunkReloadAt";

/** True when a dynamic import failed because the file is gone (a new release replaced the hashed chunks). */
export function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error);
  return /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|Unable to preload CSS/i.test(
    message
  );
}

/**
 * A tab opened before a deploy asks for chunk names that no longer exist. Reload once to pick up the new release (index
 * .html is never cached), instead of showing an error; a second failure within a minute is a real error.
 */
export function reloadOnceForNewRelease(storage: Storage = window.sessionStorage, now: number = Date.now(), reload: () => void = () => window.location.reload()): boolean {
  const last = Number(storage.getItem(RELOAD_KEY) ?? "0");
  if (Number.isFinite(last) && now - last < 60_000) return false;
  storage.setItem(RELOAD_KEY, String(now));
  reload();
  return true;
}

async function loadOrReload<M>(load: () => Promise<M>): Promise<M> {
  try {
    return await load();
  } catch (error) {
    if (isChunkLoadError(error) && reloadOnceForNewRelease()) return new Promise<M>(() => undefined);
    throw error;
  }
}

export type Preloadable = { preload: () => Promise<unknown> };

/** A lazily loaded page component with the same props as `name` in the loaded module, plus `preload()`. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyPage<M extends Record<string, any>, K extends keyof M & string>(load: () => Promise<M>, name: K): M[K] & Preloadable {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const Lazy = lazy(async () => ({ default: (await loadOrReload(load))[name] as ComponentType<any> }));
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function LazyPage(props: any) {
    return (
      <Suspense fallback={<PageSkeleton />}>
        <Lazy {...props} />
      </Suspense>
    );
  }
  LazyPage.displayName = `Lazy(${name})`;
  LazyPage.preload = () => load();
  return LazyPage as unknown as M[K] & Preloadable;
}

/** The `preload` of a component made by lazyPage, if it is one. */
export function preloadOf(component: unknown): (() => Promise<unknown>) | null {
  const p = (component as Partial<Preloadable> | null | undefined)?.preload;
  return typeof p === "function" ? p : null;
}
